<script setup lang="ts">
import { faListUl } from '@fortawesome/free-solid-svg-icons';
import { useAcquisitionStoreServer } from '@konsolenkost-de/acquisition_store_server_sdk_vue/dist/custom-fetch';
import type Uppy from '@uppy/core';
import { UppyContextProvider } from '@uppy/vue';
import { ToastProvider, TooltipProvider } from 'reka-ui';
import { onBeforeMount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterView } from 'vue-router';
import LoadingSplashScreen from './components/app/LoadingSplashScreen.vue';
import ConfirmDialog from './components/dialog/ConfirmDialog.vue';
import VersionHistoryDialog from './components/dialog/VersionHistoryDialog.vue';
import PinLoginDialog from './components/pos/PinLoginDialog.vue';
import { useChartTheme } from './composables/chart/useChartTheme';
import { useVersionHistoryDialog } from './composables/dialog/useVersionHistoryDialog';
import { useUppy } from './composables/form/useUppy';
import { useGlobalScanEvents } from './composables/scanner/useGlobalScanEvents';
import { useIdleLogout } from './composables/useIdleLogout';
import { usePwaUpdate } from './composables/usePwaUpdate';
import { useWebPush } from './composables/useWebPush';
import { isSelfHandledErrorUrl } from './constants';
import NotificationLayout from './layout/NotificationLayout.vue';
import { useAuth } from './stores/authStore.ts';
import { useNotifications } from './stores/notificationStore';
import { useServerStatus } from './stores/serverStatusStore';
import ServerDownView from './views/ServerDownView.vue';

const { VITE_API_URL = 'http://localhost:3000' } = import.meta.env;

const { t } = useI18n();
const { addResponseInterceptor } = useAcquisitionStoreServer(VITE_API_URL);
const { bootstrap } = useAuth();
const { addNotification } = useNotifications();
const { serverConnected, serverHealthy, serverDown } = useServerStatus();
const { uppy } = useUppy({ registerNotifications: true });

useWebPush();
useIdleLogout({ timeout: 5 * 60 * 1000, posOnly: true, setOfflineOnLogout: true });
useGlobalScanEvents();
useChartTheme(true);

const { show: showVersionHistory } = useVersionHistoryDialog();

const { updating } = usePwaUpdate({
  global: true,
  onUpdateAvailable: () =>
    addNotification({
      type: 'warning',
      timeout: null,
      title: t('dialog.update-available.title'),
      message: t('dialog.update-available.message'),
      actions: [
        {
          icon: faListUl,
          text: t('dialog.update-available.show-changelog'),
          onClick: showVersionHistory,
        },
      ],
    }),
});

watch(serverHealthy, (newValue) => {
  if (!newValue) {
    addNotification({
      type: 'error',
      title: t('error.server-down'),
      message: t('error.server-down-description'),
    });
  }
});

onBeforeMount(() => {
  bootstrap();
  addResponseInterceptor((url, request, response) => {
    // PIN login and POS refresh report their own failures on the keypad - an
    // expected 401 there is a wrong PIN, not something to toast at the user.
    if (isSelfHandledErrorUrl(url)) return response;

    if (response.status === 401) {
      addNotification({
        type: 'error',
        title: t('error.unauthorized'),
        message: t('error.unauthorized-description'),
      });
    } else if (response.status === 403) {
      addNotification({
        type: 'error',
        title: t('error.forbidden'),
        message: t('error.forbidden-description'),
      });
    } else if (response.status === 409) {
      addNotification({
        type: 'error',
        title: t('error.conflict'),
        message: t('error.conflict-description'),
      });
    } else if ((serverConnected.value && response.status == 400) || response.status > 404) {
      addNotification({
        type: 'error',
        title: t('error.server-error'),
        message: t('error.server-error-status-code-message', { status: response.status }),
      });
    }

    return response;
  });
});
</script>

<template>
  <ToastProvider :duration="3000">
    <TooltipProvider>
      <UppyContextProvider :uppy="uppy as unknown as Uppy">
        <ServerDownView v-if="serverDown && !updating" class="h-screen" />
        <RouterView v-else-if="serverConnected && !updating" />
        <LoadingSplashScreen v-else class="h-dvh" />
        <NotificationLayout />
        <ConfirmDialog />
        <VersionHistoryDialog />
        <PinLoginDialog />
      </UppyContextProvider>
    </TooltipProvider>
  </ToastProvider>
</template>
